import { useState } from "react";
import { Box, TextField, CircularProgress, IconButton, InputAdornment, Paper, List, ListItemButton, ListItemText } from "@mui/material";
import CustomButton from "./CustomButton";
import { Close as CloseIcon } from "@mui/icons-material";
import { useTranslation } from "react-i18next";

const CompanyValidatorInput = ({
  options, // [{label, value, email}]
  value,
  onChange,
  placeholder = "Enter company name",
}) => {
  const [input, setInput] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [matches, setMatches] = useState([]);
  const { t } = useTranslation();

  const handleValidate = () => {
    setLoading(true);
    setMatches([]);

    setTimeout(() => {
      const trimmed = input.trim().toLowerCase();
      const found = options.filter((c) =>
        c.label.toLowerCase().includes(trimmed)
      );

      if (found.length === 0) {
        setError(t("noMatchingCompany"));
        setLoading(false);
        return;
      }

      setError("");

      if (found.length === 1) {
        selectCompany(found[0]);
      } else {
        setMatches(found);
      }

      setLoading(false);
    }, 400);
  };

  const selectCompany = (company) => {
    onChange(company.value);
    setInput(company.label);
    setMatches([]);
    setError("");
  };

  const handleClear = () => {
    setInput("");
    setError("");
    setMatches([]);
    onChange("");
  };

  return (
    <Box width="100%" sx={{ position: "relative" }}>
      <Box display="flex" alignItems="center" gap={1} width="100%">
        <TextField
          fullWidth
          size="small"
          placeholder={placeholder}
          value={input}
          onChange={(e) => {
            setInput(e.target.value);
            setError("");
            setMatches([]);
          }}
          sx={{
            "& .MuiOutlinedInput-root": {
              height: 44,
              borderRadius: "6px",
              backgroundColor: "#fff",
              fontSize: "13px",
              fontFamily: "Poppins, sans-serif",
            },
            "& fieldset": {
              borderColor: "#DFE1E6",
              borderWidth: "2px",
            },
            "&:hover fieldset": {
              borderColor: "#1976d2",
            },
            "&.Mui-focused fieldset": {
              borderColor: "#1976d2",
              boxShadow: "0 0 0 2px rgba(25,118,210,0.15)",
            },
          }}
          InputProps={{
            endAdornment: input && (
              <InputAdornment position="end">
                <IconButton
                  onClick={handleClear}
                  edge="end"
                  sx={{ paddingRight: 0.5, fontSize: "16px" }}
                >
                  <CloseIcon sx={{ fontSize: "16px" }} />
                </IconButton>
              </InputAdornment>
            ),
          }}
        />

        <CustomButton
          label={
            loading ? (
              <CircularProgress size={18} sx={{ color: "white" }} />
            ) : (
              t("validate")
            )
          }
          disabled={loading}
          onClick={handleValidate}
          size="medium"
        />
      </Box>

      {/* Multiple matches dropdown */}
      {matches.length > 1 && (
        <Paper
          elevation={4}
          sx={{
            position: "absolute",
            top: "100%",
            left: 0,
            right: 0,
            zIndex: 100,
            maxHeight: 220,
            overflowY: "auto",
            borderRadius: "6px",
            mt: 0.5,
          }}
        >
          <List dense disablePadding>
            {matches.map((company) => (
              <ListItemButton
                key={company.value}
                onClick={() => selectCompany(company)}
                sx={{
                  py: 1,
                  px: 2,
                  "&:hover": { backgroundColor: "#f0f4ff" },
                }}
              >
                <ListItemText
                  primary={company.label}
                  primaryTypographyProps={{
                    fontSize: "13px",
                    fontFamily: "Poppins, sans-serif",
                    color: "#172B4D",
                  }}
                />
              </ListItemButton>
            ))}
          </List>
        </Paper>
      )}

      {/* Helper text */}
      <Box sx={{ color: "#6B778C", fontSize: "12px", mt: 0.5, fontFamily: "Poppins, sans-serif" }}>
        {t("helperText")}
      </Box>

      {error && (
        <Box sx={{ color: "red", fontSize: "12px", mt: 0.5 }}>{error}</Box>
      )}
    </Box>
  );
};

export default CompanyValidatorInput;
